import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../../contexts/RoleContext";
import EditChildModal from "../../components/Dashboard/EditChildModal";
import { CARE_SNAPSHOT_STEPS } from "../../constants/careSnapshotSections";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";
import { getMedicationReviewNavigationContext } from "../../utils/medicationReviewNavigation";
import { ACTIVE_CHILD_STORAGE_KEY } from "../../components/Dashboard/shared/DashboardViewContext";
import ChildShellBottomNav from "../../components/Dashboard/shared/ChildShellBottomNav";
import { getChildAccentTheme } from "../../components/Dashboard/shared/childAccentTheme";
import { CARE_PROFILE_SECTION_IDS } from "../../utils/careProfileSections";

const LAST_EDIT_CHILD_STORAGE_KEY = "lifelog:last-edit-child-id";

const getSessionChildId = () => {
  try {
    return window.sessionStorage.getItem(LAST_EDIT_CHILD_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const setSessionChildId = (childId = "") => {
  try {
    window.sessionStorage.setItem(LAST_EDIT_CHILD_STORAGE_KEY, childId);
  } catch (error) {
    // Route state and active-child events still carry the selected profile.
  }
};

const EditChildPage = () => {
  const { childId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { childrenWithAccess, getUserRoleForChild, loading } = useRole();
  const stateChildId = location.state?.childId;
  const [selectedChildId, setSelectedChildId] = useState(() => stateChildId || getSessionChildId() || childId || null);
  const resolvedChildId = selectedChildId || stateChildId || childId || null;

  const child = childrenWithAccess?.find((item) => item.id === resolvedChildId) || null;
  const childAccent = getChildAccentTheme(child || {}, { children: childrenWithAccess || [] });
  const shouldOpenMedicationManager = Boolean(location.state?.openChildMedicationManager);
  const shouldOpenAllergiesManager = Boolean(location.state?.openAllergiesManager);
  const medicationReviewNavigation = getMedicationReviewNavigationContext(location.state || {});
  const profileReturnTo = location.state?.profileReturnTo || '';
  const profileReturnState = location.state?.profileReturnState || null;
  const initialStep = shouldOpenMedicationManager || shouldOpenAllergiesManager
    ? CARE_SNAPSHOT_STEPS.HEALTH_CARE
    : Number.isFinite(Number(location.state?.initialStep))
      ? Number(location.state?.initialStep)
      : 0;

  useEffect(() => {
    const routeChildId = stateChildId || childId || "";
    if (routeChildId) {
      setSelectedChildId(routeChildId);
      setSessionChildId(routeChildId);
    }
  }, [childId, location.key, stateChildId]);

  useEffect(() => {
    if (resolvedChildId) {
      setSessionChildId(resolvedChildId);
    }
  }, [resolvedChildId]);

  useEffect(() => {
    const handleActiveChildChanged = (event) => {
      const nextChildId = event?.detail?.childId
        || window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY)
        || "";

      if (nextChildId) {
        setSelectedChildId(nextChildId);
        setSessionChildId(nextChildId);
      }
    };

    window.addEventListener("captureez:active-child-changed", handleActiveChildChanged);
    return () => window.removeEventListener("captureez:active-child-changed", handleActiveChildChanged);
  }, []);

  if (childId) {
    return (
      <Navigate
        to={CARE_PROFILE_ROUTE}
        replace
        state={{
          ...location.state,
          childId,
          initialStep,
        }}
      />
    );
  }

  if (loading && !child) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && !child) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleBottomNavigation = (itemId) => {
    if (itemId === "home") {
      navigate("/home", { state: { selectedChildId: resolvedChildId || null } });
      return;
    }

    if (itemId === "profile") {
      navigate(CARE_PROFILE_ROUTE, {
        state: { childId: resolvedChildId || null },
      });
      return;
    }
    navigate("/dashboard", {
      state: {
        selectedChildId: resolvedChildId || null,
        childShellTarget: itemId === "tools" ? "tools" : "today",
      },
    });
  };

  return (
    <EditChildModal
      key={location.key}
      child={child}
      userRole={child ? getUserRoleForChild?.(child.id) : null}
      initialStep={initialStep}
      initialProfileChapterId={location.state?.profileChapterViewId || null}
      initialProfileSectionId={
        shouldOpenMedicationManager
          ? CARE_PROFILE_SECTION_IDS.MEDICATION
          : shouldOpenAllergiesManager
            ? CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD
            : ''
      }
      initialProfileSectionLabel={
        shouldOpenMedicationManager
          ? 'Medications'
          : shouldOpenAllergiesManager
            ? 'Allergies'
            : ''
      }
      openInboundEmailOnLoad={Boolean(location.state?.openInboundEmail)}
      onDirectProfileChapterBack={profileReturnTo ? () => navigate(profileReturnTo, {
        replace: true,
        state: profileReturnState,
      }) : null}
      openMedicationEditorOnLoad={shouldOpenMedicationManager}
      medicationReviewNavigation={medicationReviewNavigation}
      onOpenMedicationReview={(source) => navigate(`/children/${resolvedChildId}/medications/review/${source.id}`, {
        state: { source },
      })}
      onOpenMedicationReminders={(medication) => navigate(
        `/children/${resolvedChildId}/medication-reminders${medication?.id ? `?medicationId=${encodeURIComponent(medication.id)}` : ''}`,
        {
          state: {
            returnTo: CARE_PROFILE_ROUTE,
            returnState: { childId: resolvedChildId, openChildMedicationManager: true },
          },
        }
      )}
      bottomNavigation={(
        <ChildShellBottomNav
          activeItem="profile"
          accent={childAccent}
          onSelect={handleBottomNavigation}
        />
      )}
    />
  );
};

export default EditChildPage;
